import { LINK_STATUS_IDS, STATUS_IDS, statusItemId } from '../statuses/expiry.js';
import { REDUCE_DAMAGE_ROLL } from './weapon-attack-extra-damage.js';

type Row = Record<string, unknown>;
/** Damage-time work belongs to the shared weapon/ability frame, not an item-id whitelist.
 * Speeds, skills, aura geometry and expiry hooks alone do nothing when their holder is hit. */
export const DAMAGE_STATUS_LAYERS: ReadonlySet<string> = new Set([
  'triggers_when_caster_takes_any_damage', 'triggers_when_caster_takes_damage_type',
  'triggers_when_target_takes_any_damage', 'triggers_when_target_takes_damage_type',
  'target_retries_contesting_roll_on_damage_taken',
  'damage_type_resistance', 'damage_type_immunity', 'damage_type_vulnerability',
  'spell_damage_resistance', 'spell_damage_vulnerability', 'damage_mitigation',
  'transfer_damage_percentage_to_caster_of_status_effect', 'all_incoming_damage_changed_by_percentage',
]);
const NATIVE_DAMAGE_STATUSES: ReadonlySet<string> = new Set([
  '095BC6BC-06F6-74E0-2B8A-FC1F32AF809B', // Sleeping
  'E7FDB796-26DA-E0F5-8F88-C21FE01D923B', // Concentrating
  'DD4C0995-C3D2-43A3-CFC7-226C3005274C', // Charmed
  'D2E7BA2C-562D-D4AC-7F71-13E4AC540484', // Dominated
  '47667406-1B72-9D60-310B-D8CF6BFE397D', // Hidden
]);
export function damageStatusScope(status: Row, definition: Row, read: {
  entries: (row: Row, key: string) => unknown[];
  value: (row: Row, key: string) => unknown;
}): 'inert' | 'reactive' {
  if (NATIVE_DAMAGE_STATUSES.has(statusItemId(status))) return 'reactive';
  if (read.value(definition, 'roll_type') === REDUCE_DAMAGE_ROLL) return 'reactive';
  return [...DAMAGE_STATUS_LAYERS].some(key => read.entries(definition, key).length > 0) ? 'reactive' : 'inert';
}

/** A damage-time removal/application that would change a walk already priced and its swept zones needs a combined path plan.
 * Only a due change is tested; merely carrying a speed or aura status never refuses movement. */
export function damageMovementStatusFailure(changes: ReadonlyArray<{ status_item_id: string; target: string; status_id?: string }>, read: {
  definition: (id: string) => Row | null; entries: (row: Row, key: string) => unknown[];
}, walk: { targets: ReadonlySet<string>; removedZoneSources: ReadonlySet<string> }): string | null {
  for (const change of changes) {
    // The encounter-wide exit sweep still uses its source anchors. Other creatures' ordinary status changes
    // do not alter this walk's price, footprint or links and settle exactly as they do on a stationary hit.
    if (change.status_id && walk.removedZoneSources.has(change.status_id.toUpperCase()))
      return 'reaction-movement-status-change:persistent_aoe';
    if (!walk.targets.has(change.target.toUpperCase())) continue;
    if (LINK_STATUS_IDS.has(statusItemId(change)) && statusItemId(change) !== STATUS_IDS.concentrating)
      return 'reaction-movement-link-change:' + statusItemId(change);
    const definition = read.definition(statusItemId(change));
    if (!definition) return 'reaction-movement-status-definition:' + statusItemId(change);
    const keys = ['speed_walking', 'speed_flying', 'speed_swimming', 'speed_climbing', 'speed_burrowing', 'teleport', 'size',
      'no_actions', 'persistent_aoe', 'aoe_follows_target', 'triggers_on_move'];
    const layer = keys.find(key => read.entries(definition, key).length > 0);
    if (layer) return 'reaction-movement-status-change:' + layer;
  }
  return null;
}
